/*
 * FilthyBass — contact form: international phone + English-only validation.
 *
 * Why this file exists
 * --------------------
 * The old field had `pattern="[0-9\-]*"`, which rejects `+`. When a pattern (or
 * any constraint) fails, the browser shows its OWN validation bubble, and that
 * text is localised to the browser's UI language — a Dutch-Chrome visitor on an
 * English store got a Dutch error. No HTML attribute changes that; `title` only
 * appends to the browser's own string.
 *
 * So the only reliable fix is to stop the browser from reporting at all:
 * set `novalidate` here and render every message ourselves.
 *
 * `novalidate` is set from JS on purpose. If this script fails to load, the
 * markup still has `required` and `type="email"`, so the browser validates
 * natively — a localised message beats no validation at all.
 */
(function () {
  'use strict';

  var STRINGS = {
    required: 'This field is required.',
    email: 'Enter a valid email address, like name@example.com.',
    phoneDigits: 'Enter a phone number using digits only, for example 6 10089029.',
    phoneShort: 'That number looks too short. Check it and try again.',
    phoneLong: 'That number looks too long. Check it and try again.'
  };

  function setError(input, message) {
    var error = document.getElementById(input.id + '-error');

    input.setCustomValidity(message || '');

    if (!error) return;

    if (message) {
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', error.id);
    } else {
      error.textContent = '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  /*
   * Deliberately permissive. This form is a front door, not a billing system —
   * a real customer typing a slightly odd number should get through, and the
   * cost of a false rejection is a lost enquiry. We only catch the cases that
   * are certainly wrong: letters, and lengths outside E.164's own bounds.
   */
  function validatePhone(nationalInput, dialSelect, combinedInput) {
    var raw = nationalInput.value.trim();

    /*
     * Clear first, always. The hidden field is what actually gets POSTed, so it
     * must never outlive the input it was derived from — otherwise editing a
     * good number into a bad one leaves the old number staged for submission.
     */
    combinedInput.value = '';

    if (!raw) {
      setError(nationalInput, '');
      return true;
    }

    if (/[^\d\s()./+-]/.test(raw)) {
      setError(nationalInput, STRINGS.phoneDigits);
      return false;
    }

    var digits = raw.replace(/\D/g, '');
    var selected = dialSelect.options[dialSelect.selectedIndex];
    var dial = (selected && selected.getAttribute('data-dial')) || '';

    // A leading trunk zero is national-only notation: 06… is +31 6…, not +310 6….
    digits = digits.replace(/^0+/, '');

    // Someone may paste the full international number into the national box.
    if (dial && digits.indexOf(dial) === 0 && digits.length > dial.length) {
      digits = digits.slice(dial.length).replace(/^0+/, '');
    }

    if (!digits) {
      setError(nationalInput, STRINGS.phoneDigits);
      return false;
    }

    // E.164 allows 15 digits total including the country code, and no national
    // number anywhere is shorter than 4.
    if (digits.length < 4) {
      setError(nationalInput, STRINGS.phoneShort);
      return false;
    }

    if (dial.length + digits.length > 15) {
      setError(nationalInput, STRINGS.phoneLong);
      return false;
    }

    setError(nationalInput, '');
    combinedInput.value = '+' + dial + digits;
    return true;
  }

  function validateRequired(input) {
    if (!input.value.trim()) {
      setError(input, STRINGS.required);
      return false;
    }
    setError(input, '');
    return true;
  }

  function validateEmail(input) {
    var value = input.value.trim();

    if (!value) {
      setError(input, STRINGS.required);
      return false;
    }

    // Intentionally loose: catches the typo-shaped mistakes without rejecting
    // the long tail of technically-valid addresses.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setError(input, STRINGS.email);
      return false;
    }

    setError(input, '');
    return true;
  }

  function init(form) {
    if (form.dataset.fbContactBound === 'true') return;
    form.dataset.fbContactBound = 'true';

    // The whole point: suppress the browser's localised bubble.
    form.setAttribute('novalidate', 'novalidate');

    var name = form.querySelector('#ContactForm-name');
    var email = form.querySelector('#ContactForm-email');
    var body = form.querySelector('#ContactForm-body');
    var national = form.querySelector('[data-fb-phone-national]');
    var dial = form.querySelector('[data-fb-phone-dial]');
    var combined = form.querySelector('[data-fb-phone-combined]');

    var hasPhone = national && dial && combined;

    function runPhone() {
      return hasPhone ? validatePhone(national, dial, combined) : true;
    }

    // Validate on blur, but re-validate on input only once a field has already
    // errored — nagging someone mid-typing is how you get an abandoned form.
    function bind(input, validator) {
      if (!input) return;
      input.addEventListener('blur', function () {
        validator();
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') validator();
      });
    }

    bind(email, function () {
      return validateEmail(email);
    });
    bind(body, function () {
      return validateRequired(body);
    });
    bind(national, runPhone);

    if (dial) {
      dial.addEventListener('change', runPhone);
    }

    form.addEventListener('submit', function (event) {
      // Run every validator before short-circuiting, so the customer sees all
      // the problems at once rather than one per submit.
      var results = [
        email ? validateEmail(email) : true,
        body ? validateRequired(body) : true,
        runPhone()
      ];

      if (results.indexOf(false) !== -1) {
        event.preventDefault();
        var firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      // Name is optional but should still reach the merchant trimmed.
      if (name) name.value = name.value.trim();
    });
  }

  function boot() {
    document.querySelectorAll('.fb-contact__form').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
